import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled, { css, keyframes } from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { Button } from 'atoms';

import { TodayLinkPanel } from './TodayLinkPanel';

export interface ILessonZoomJoin {
  /** hasJoinLink=false hides the button (spec 0013). */
  showJoinButton: boolean;
  /** Lesson happens today — the button gets the pulsing accent. */
  isToday: boolean;
  /** Outside the lesson's join window (students only) — button is inert. */
  joinDisabled: boolean;
  /** Why it's inert, e.g. «Занятие начнётся 30.07, 19:00». Empty when active. */
  joinHint: string;
  joinLoading: boolean;
  onJoin: () => void;
  isTeacher: boolean;
  linkOverrideActive: boolean;
  setLoading: boolean;
  clearLoading: boolean;
  onSetLink: (joinUrl: string) => Promise<boolean>;
  onClearLink: () => void;
  className?: string;
}

/**
 * Spec 0013: "join the live lesson" block rendered above the lesson tabs —
 * the student/teacher join button plus the teacher's temporary-link panel.
 */
export const LessonZoomJoin = memo(
  ({
    showJoinButton,
    isToday,
    joinDisabled,
    joinHint,
    joinLoading,
    onJoin,
    isTeacher,
    linkOverrideActive,
    setLoading,
    clearLoading,
    onSetLink,
    onClearLink,
    className,
  }: ILessonZoomJoin) => {
    const { t } = useTranslation();

    return (
      <JoinWrapper className={className}>
        {showJoinButton && (
          <JoinRow>
            <JoinButton
              $pulsing={isToday && !joinDisabled}
              onClick={onJoin}
              isLoading={joinLoading}
              disabled={joinLoading || joinDisabled}>
              {t('lesson.join.joinButton')}
            </JoinButton>
            {isToday && !joinDisabled && <TodayBadge>{t('lesson.join.joinToday')}</TodayBadge>}
            {joinHint && <JoinHint>{joinHint}</JoinHint>}
          </JoinRow>
        )}

        {!showJoinButton && isTeacher && (
          <NotConfigured>{t('lesson.join.notConfigured')}</NotConfigured>
        )}

        {isTeacher && (
          <TodayLinkPanel
            linkOverrideActive={linkOverrideActive}
            setLoading={setLoading}
            clearLoading={clearLoading}
            onSetLink={onSetLink}
            onClearLink={onClearLink}
          />
        )}
      </JoinWrapper>
    );
  },
);

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${Colors.main}99;
  }
  70% {
    box-shadow: 0 0 0 12px ${Colors.main}00;
  }
  100% {
    box-shadow: 0 0 0 0 ${Colors.main}00;
  }
`;

const JoinWrapper = styled.div`
  width: 100%;
`;

const JoinRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const JoinButton = styled(Button)<{ $pulsing: boolean }>`
  ${({ $pulsing }) =>
    $pulsing &&
    css`
      animation: ${pulse} 2s infinite;
    `}
`;

const TodayBadge = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const JoinHint = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const NotConfigured = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;
